import { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Table as NextUiTable,
  Pagination,
  PaginationItemType,
  Select,
  SelectItem,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  cn,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BiSolidUpArrow } from "react-icons/bi";
import { ChevronIcon } from "../assets/icons/audio-icons";
import { useAppContext } from "../contexts/AppContext";

const classNames = {
  wrapper: ["h-auto w-full bg-transparent p-0", "justify-between", "gap-y-0"],
  th: [
    "w-full h-auto px-3",
    "text-default-800 bg-transparent",
    "rounded-0",
    "shadow-none",
    "first:rounded-none",
    "last:rounded-none",
  ],
  table: ["table-auto md:table-fixed border-separate border-spacing-y-3"],
  td: [
    "bg-white max-w-min",
    "whitespace-nowrap",
    "group-data-[first=true]:first:rounded-l-full",
    "group-data-[first=true]:last:rounded-r-full",
    "group-data-[middle=true]:first:rounded-l-full",
    "group-data-[middle=true]:last:rounded-r-full",
    "group-data-[last=true]:first:rounded-l-full",
    "group-data-[last=true]:last:rounded-r-full",
  ],
};

const Table = ({
  columns = [],
  items = [],
  emptyContent = "No records to display",
  loading = false,
  renderCell,
  customClassname = "",
  headerClassName = "",
  stickyHeader = false,
  stripped = false,
  onRowAction = null,
  defaultDescriptor = { column: "department", direction: "descending" },
  handleOnSort = () => {},
  showTopContent = true,
  showBottomContent = true,
  isCompact = false,
  algoliaPaginationEnabled = false,
  handleOnPageChanged = () => {},
}) => {
  const { t } = useTranslation();
  const { algoliaTotalItems, algoliaTotalPages } = useSelector((state) => state.calls.calls);
  const [sortDescriptor, setSortDescriptor] = useState(defaultDescriptor);
  const { searchText } = useAppContext();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  useEffect(() => {
    setPage(1);
    handleOnPageChanged(1, rowsPerPage);
  }, [rowsPerPage, searchText]);

  const pages = useMemo(() => {
    return Math.ceil(items.length / rowsPerPage);
  }, [items, rowsPerPage]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (handleOnSort) {
        return handleOnSort(a, b, sortDescriptor);
      }
      const first = a[sortDescriptor.column];
      const second = b[sortDescriptor.column];
      const cmp = first < second ? -1 : first > second ? 1 : 0;
      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, items]);

  const itemsPerPage = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedItems.slice(start, end);
  }, [page, sortedItems, rowsPerPage]);

  const topContent = () => {
    const nbPageOptions = [12, 25, 50, 100];

    return (
      <div className="w-full py-2 px-3">
        <div className="flex items-center space-x-2 text-default-700">
          <span className="text-xs">{t("Showing")}</span>
          <Select
            aria-label="Select items per page"
            placeholder="Select items per page"
            selectedKeys={[rowsPerPage.toString()]}
            defaultSelectedKeys={["12"]}
            className="max-w-[78px] p-0"
            color="primary"
            classNames={{
              trigger: "bg-white",
              selectorIcon: "w-2.5",
              label: "hidden",
              innerWrapper: "p-0",
              mainWrapper: "p-0 h-8",
              value: "text-sm leading-none text-primary font-medium font-PlusJakartaSans",
            }}
            size="sm"
            onSelectionChange={(key) => {
              setRowsPerPage(Number(key.values().next().value));
            }}
            selectorIcon={<BiSolidUpArrow />}
          >
            {nbPageOptions.map((nbPageOption) => (
              <SelectItem
                key={`${nbPageOption}`}
                value={nbPageOption}
              >
                {`${nbPageOption}`}
              </SelectItem>
            ))}
          </Select>
          <span className="text-default-700 text-xs">
            {" "}
            {t("calls out of")} {items.length}
          </span>
        </div>
      </div>
    );
  };

  const renderPaginationItem = ({
    ref,
    key,
    value,
    isActive,
    onNext,
    onPrevious,
    setPage,
    className,
  }) => {
    if (value === PaginationItemType.NEXT) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "items-center space-x-1 bg-transparent min-w-20 w-auto h-8 ml-3",
            page >= pages && "text-gray-200"
          )}
          onClick={onNext}
          disabled={page >= pages}
        >
          {t("Next")}{" "}
          <ChevronIcon
            className="w-8 rotate-180"
            size={18}
          />
        </button>
      );
    }
    if (value === PaginationItemType.PREV) {
      return (
        <button
          type="button"
          key={key}
          className={cn(
            className,
            "flex items-center space-x-1 bg-transparent min-w-20 w-auto h-8 mr-3",
            page <= 1 && "text-gray-200"
          )}
          onClick={onPrevious}
          disabled={page <= 1}
        >
          <ChevronIcon
            className="w-8"
            size={18}
          />{" "}
          {t("Prev")}
        </button>
      );
    }
    if (value === PaginationItemType.DOTS) {
      return (
        <button
          type="button"
          key={key}
          className={className}
        >
          ...
        </button>
      );
    }
    // cursor is the default item
    return (
      <button
        type="button"
        key={key}
        ref={ref}
        className={cn(className)}
        onClick={() => setPage(value)}
      >
        {value}
      </button>
    );
  };

  const bottomContent = () => {
    return (
      <div className="flex justify-center items-center pt-4 px-2">
        {!loading && (
          <Pagination
            showControls
            classNames={{
              cursor: "rounded-full",
            }}
            color="primary"
            page={page}
            total={pages}
            variant="light"
            onChange={async (new_page) => {
              if (algoliaPaginationEnabled) {
                await handleOnPageChanged(new_page, rowsPerPage);
              }
              setPage(new_page);
            }}
            renderItem={renderPaginationItem}
          />
        )}
      </div>
    );
  };

  return (
    <NextUiTable
      isHeaderSticky={stickyHeader}
      aria-label="Example table with dynamic content"
      shadow="none"
      radius="none"
      fullWidth
      isCompact={isCompact}
      isStriped={stripped}
      topContent={showTopContent ? topContent() : null}
      bottomContent={showBottomContent ? bottomContent() : null}
      className={customClassname}
      onRowAction={onRowAction}
      classNames={classNames}
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      removeWrapper
    >
      <TableHeader
        shadow="none"
        columns={columns}
      >
        {(column) => (
          <TableColumn
            key={column.key}
            align={column.uid === "actions" ? "center" : "start"}
            allowsSorting={column.sortable}
            className={`text-xs font-normal ${headerClassName}`}
          >
            {column.label}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody
        items={itemsPerPage}
        emptyContent={emptyContent}
        isLoading={loading}
        loadingContent={
          <div className="flex flex-col justify-center items-center w-full h-96 m-auto text-sm">
            <div className="bg-white z-10 p-4 px-6 rounded-2xl shadow-2xl border border-gray-100">
              <CircularProgress
                aria-label="Loading..."
                color="primary"
                label="Loading..."
              />
            </div>
          </div>
        }
      >
        {(item) => (
          <TableRow
            key={item.id}
            className={onRowAction ? "cursor-pointer" : "cursor-default"}
          >
            {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
          </TableRow>
        )}
      </TableBody>
    </NextUiTable>
  );
};

export default Table;
